import { useEffect } from "react";

import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";

import type { useIdentityProviderSources_query$key } from "./__generated__/useIdentityProviderSources_query.graphql";
import type { useIdentityProviderSourcesQuery } from "./__generated__/useIdentityProviderSourcesQuery.graphql";
import type { useIdentityProviderSourcesRefetchQuery } from "./__generated__/useIdentityProviderSourcesRefetchQuery.graphql";

/** Cloud organizations fetched per page while loading them all. */
const CLOUD_ORG_PAGE = 100;

/**
 * Loads every identity-provider source — all SAML providers and cloud
 * organizations — for the RBAC directories. Cloud orgs are a connection, so
 * every page is fetched, not just the first. Returns the raw sources (the field
 * superset both directories need); each caller maps them to its own shape.
 */
export function useIdentityProviderSources() {
  const queryData = useLazyLoadQuery<useIdentityProviderSourcesQuery>(
    graphql`
      query useIdentityProviderSourcesQuery($first: Int) {
        ...useIdentityProviderSources_query @arguments(first: $first)
      }
    `,
    { first: CLOUD_ORG_PAGE },
  );

  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment<
    useIdentityProviderSourcesRefetchQuery,
    useIdentityProviderSources_query$key
  >(
    graphql`
      fragment useIdentityProviderSources_query on Query
      @argumentDefinitions(first: { type: "Int" }, after: { type: "String" })
      @refetchable(queryName: "useIdentityProviderSourcesRefetchQuery") {
        samlProviders {
          displayName
          id
          idpAlias
          idpNamespace
          idpType
          name
        }
        cloudOrganizations(first: $first, after: $after)
          @connection(key: "useIdentityProviderSources_cloudOrganizations") {
          edges {
            node {
              id
              idpAlias
              idpNamespace
              idpType
              key
              name
              provider
            }
          }
        }
      }
    `,
    queryData,
  );

  // Load every cloud org, not just the first page — the directories group them
  // all, so a fixed cap would silently omit providers.
  useEffect(() => {
    if (hasNext && !isLoadingNext) {
      loadNext(CLOUD_ORG_PAGE);
    }
  }, [hasNext, isLoadingNext, loadNext]);

  // Map to plain records here (rather than returning the raw fragment data) so
  // every queried field is read in this module — Relay's colocation lint wants
  // the query and its field usage together.
  const samlProviders = data.samlProviders.map((provider) => ({
    id: provider.id,
    name: provider.name,
    displayName: provider.displayName,
    idpAlias: provider.idpAlias,
    idpType: provider.idpType,
    idpNamespace: provider.idpNamespace,
  }));

  const cloudOrganizations = (data.cloudOrganizations.edges ?? [])
    .map((edge) => edge?.node)
    .filter((node): node is NonNullable<typeof node> => Boolean(node))
    .map((node) => ({
      id: node.id,
      name: node.name,
      provider: node.provider,
      key: node.key,
      idpAlias: node.idpAlias,
      idpType: node.idpType,
      idpNamespace: node.idpNamespace,
    }));

  return {
    samlProviders,
    cloudOrganizations,
    /** True while more cloud-org pages are still loading. */
    loading: isLoadingNext,
  };
}
